// Comprehensive Error Logging System
// Provides structured error logging, monitoring, and reporting

import React from 'react';

export interface ErrorContext {
  userId?: string;
  component?: string;
  action?: string;
  route?: string;
  userAgent?: string;
  timestamp?: string;
  sessionId?: string;
  buildVersion?: string;
  environment?: string;
  additionalData?: Record<string, any>;
}

export interface ErrorLog {
  id: string;
  level: 'error' | 'warning' | 'info' | 'debug';
  message: string;
  stack?: string;
  context: ErrorContext;
  timestamp: string;
  fingerprint?: string;
}

export interface ErrorMetrics {
  errorCount: number;
  warningCount: number;
  uniqueErrors: number;
  topErrors: Array<{ message: string; count: number }>;
  errorsByComponent: Record<string, number>;
  errorsByUser: Record<string, number>;
}

class ErrorLogger {
  private logs: ErrorLog[] = [];
  private maxLogs: number = 1000;
  private sessionId: string;
  private environment: string;
  private buildVersion: string;
  private isEnabled: boolean = true;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.environment = process.env.NODE_ENV || 'development';
    this.buildVersion = process.env.REACT_APP_VERSION || '1.0.0';
    
    // Set up global error handlers
    this.setupGlobalErrorHandlers();
    
    // Clean up old logs periodically
    this.startLogCleanup();
  }

  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private setupGlobalErrorHandlers(): void {
    // Global JavaScript errors
    window.addEventListener('error', (event) => {
      this.logError(new Error(event.message), {
        component: 'Global',
        action: 'JavaScript Error',
        additionalData: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      });
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError(new Error(`Unhandled Promise Rejection: ${event.reason}`), {
        component: 'Global',
        action: 'Promise Rejection'
      });
    });

    // React error boundary integration
    if (typeof window !== 'undefined') {
      (window as any).__errorLogger = this;
    }
  }

  private startLogCleanup(): void {
    // Clean up old logs every 10 minutes
    setInterval(() => {
      if (this.logs.length > this.maxLogs) {
        this.logs = this.logs.slice(-this.maxLogs);
      }
    }, 10 * 60 * 1000);
  }

  private createFingerprint(error: Error, context: ErrorContext): string {
    // Create a unique fingerprint for grouping similar errors
    const message = error.message || 'Unknown error';
    const component = context.component || 'Unknown';
    const action = context.action || 'Unknown';
    
    return btoa(`${message}-${component}-${action}`);
  }

  public logError(error: Error, context: ErrorContext = {}): void {
    if (!this.isEnabled) return;

    const errorLog: ErrorLog = {
      id: this.generateId(),
      level: 'error',
      message: error.message || 'Unknown error occurred',
      stack: error.stack,
      context: {
        ...context,
        userId: context.userId || this.getUserId(),
        route: context.route || window.location.pathname,
        userAgent: context.userAgent || navigator.userAgent,
        timestamp: context.timestamp || new Date().toISOString(),
        sessionId: this.sessionId,
        buildVersion: this.buildVersion,
        environment: this.environment
      },
      timestamp: new Date().toISOString(),
      fingerprint: this.createFingerprint(error, context)
    };

    this.logs.push(errorLog);
    
    // Send to console in development
    if (this.environment === 'development') {
      console.error('Error logged:', errorLog);
    }

    // Send to external service in production
    if (this.environment === 'production') {
      this.sendToExternalService(errorLog);
    }

    // Store in localStorage for offline capability
    this.persistToStorage(errorLog);
  }

  public logWarning(message: string, context: ErrorContext = {}): void {
    if (!this.isEnabled) return;

    const warningLog: ErrorLog = {
      id: this.generateId(),
      level: 'warning',
      message,
      context: {
        ...context,
        userId: context.userId || this.getUserId(),
        route: context.route || window.location.pathname,
        sessionId: this.sessionId,
        buildVersion: this.buildVersion,
        environment: this.environment
      },
      timestamp: new Date().toISOString()
    };

    this.logs.push(warningLog);

    if (this.environment === 'development') {
      console.log('Warning logged:', warningLog);
    }
  }

  public logInfo(message: string, context: ErrorContext = {}): void {
    if (!this.isEnabled) return;

    const infoLog: ErrorLog = {
      id: this.generateId(),
      level: 'info',
      message,
      context: {
        ...context,
        userId: context.userId || this.getUserId(),
        route: context.route || window.location.pathname,
        sessionId: this.sessionId,
        buildVersion: this.buildVersion,
        environment: this.environment
      },
      timestamp: new Date().toISOString()
    };

    this.logs.push(infoLog);

    if (this.environment === 'development') {
      console.info('Info logged:', infoLog);
    }
  }

  public logCalculationError(
    calculatorType: string, 
    operation: string, 
    inputs: any, 
    error: Error
  ): void {
    this.logError(error, {
      component: `${calculatorType}Calculator`,
      action: `Calculation-${operation}`,
      additionalData: {
        inputs: this.sanitizeInputs(inputs),
        calculatorType,
        operation
      }
    });
  }

  public logValidationError(
    field: string, 
    value: any, 
    validationRule: string, 
    component: string
  ): void {
    this.logWarning(`Validation failed for ${field}`, {
      component,
      action: 'Validation',
      additionalData: {
        field,
        value: this.sanitizeValue(value),
        validationRule
      }
    });
  }

  public logUserAction(
    action: string, 
    component: string, 
    additionalData?: any
  ): void {
    this.logInfo(`User action: ${action}`, {
      component,
      action: 'UserAction',
      additionalData: this.sanitizeInputs(additionalData)
    });
  }

  public logApiError(
    endpoint: string, 
    method: string, 
    statusCode: number, 
    error: Error
  ): void {
    this.logError(error, {
      component: 'API',
      action: `${method} ${endpoint}`,
      additionalData: {
        endpoint,
        method,
        statusCode
      }
    });
  }

  private sanitizeInputs(inputs: any): any {
    if (!inputs) return null;
    
    // Remove sensitive fields
    const sanitized = { ...inputs };
    const sensitiveFields = ['password', 'token', 'apiKey', 'secret', 'auth'];
    
    const removeSensitive = (obj: any) => {
      if (typeof obj !== 'object' || obj === null) return obj;
      
      const result = Array.isArray(obj) ? [] : {};
      
      for (const [key, value] of Object.entries(obj)) {
        if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
          (result as any)[key] = '[REDACTED]';
        } else if (typeof value === 'object') {
          (result as any)[key] = removeSensitive(value);
        } else {
          (result as any)[key] = value;
        }
      }
      
      return result;
    };
    
    return removeSensitive(sanitized);
  }

  private sanitizeValue(value: any): any {
    if (typeof value === 'string' && value.length > 100) {
      return value.substring(0, 100) + '...';
    }
    return value;
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private getUserId(): string | undefined {
    // Try to get user ID from various sources
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        return user.id || user.email || undefined;
      }
    } catch (e) {
      // Ignore errors
    }
    return undefined;
  }

  private async sendToExternalService(log: ErrorLog): Promise<void> {
    try {
      // In a real application, you would send to services like:
      // - Sentry
      // - LogRocket
      // - Datadog
      // - Custom logging endpoint
      
      await fetch('/api/logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(log)
      });
    } catch (error) {
      // Silently fail to avoid infinite error loops
      console.log('Failed to send log to external service:', error);
    }
  }

  private persistToStorage(log: ErrorLog): void {
    try {
      const key = `error_log_${log.id}`;
      sessionStorage.setItem(key, JSON.stringify(log));
      
      // Clean up old logs from storage
      this.cleanupStorage();
    } catch (error) {
      // Storage might be full, ignore
    }
  }

  private cleanupStorage(): void {
    try {
      const keys = Object.keys(sessionStorage).filter(key => key.startsWith('error_log_'));
      if (keys.length > 50) {
        // Remove oldest logs
        keys.slice(0, keys.length - 50).forEach(key => {
          sessionStorage.removeItem(key);
        });
      }
    } catch (error) {
      // Ignore errors
    }
  }

  public getLogs(filter?: { level?: string; component?: string; limit?: number }): ErrorLog[] {
    let filteredLogs = this.logs;

    if (filter?.level) {
      filteredLogs = filteredLogs.filter(log => log.level === filter.level);
    }

    if (filter?.component) {
      filteredLogs = filteredLogs.filter(log => log.context.component === filter.component);
    }

    if (filter?.limit) {
      filteredLogs = filteredLogs.slice(-filter.limit);
    }

    return filteredLogs;
  }

  public getMetrics(): ErrorMetrics {
    const errorLogs = this.logs.filter(log => log.level === 'error');
    const warningLogs = this.logs.filter(log => log.level === 'warning');

    // Count errors by message
    const errorCounts: Record<string, number> = {};
    errorLogs.forEach(log => {
      errorCounts[log.message] = (errorCounts[log.message] || 0) + 1;
    });

    // Count errors by component
    const componentCounts: Record<string, number> = {};
    errorLogs.forEach(log => {
      const component = log.context.component || 'Unknown';
      componentCounts[component] = (componentCounts[component] || 0) + 1;
    });

    // Count errors by user
    const userCounts: Record<string, number> = {};
    errorLogs.forEach(log => {
      const userId = log.context.userId || 'Anonymous';
      userCounts[userId] = (userCounts[userId] || 0) + 1;
    });

    // Get top errors
    const topErrors = Object.entries(errorCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([message, count]) => ({ message, count }));

    return {
      errorCount: errorLogs.length,
      warningCount: warningLogs.length,
      uniqueErrors: Object.keys(errorCounts).length,
      topErrors,
      errorsByComponent: componentCounts,
      errorsByUser: userCounts
    };
  }

  public exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  public clearLogs(): void {
    this.logs = [];
    
    // Clear from storage
    const keys = Object.keys(sessionStorage).filter(key => key.startsWith('error_log_'));
    keys.forEach(key => sessionStorage.removeItem(key));
  }

  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  public isLoggingEnabled(): boolean {
    return this.isEnabled;
  }
}

// Create singleton instance
const errorLogger = new ErrorLogger();

// React Error Boundary integration
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ComponentType<{ error: Error }> },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    errorLogger.logError(error, {
      component: 'ErrorBoundary',
      action: 'ComponentError',
      additionalData: {
        componentStack: errorInfo.componentStack
      }
    });
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback;
      if (FallbackComponent && this.state.error) {
        return React.createElement(FallbackComponent, { error: this.state.error });
      }
      
      return React.createElement('div', 
        { style: { padding: '2rem', textAlign: 'center' } },
        React.createElement('h2', null, 'Something went wrong'),
        React.createElement('p', null, 'We\'ve been notified about this error and will fix it soon.'),
        React.createElement('button', 
          { onClick: () => this.setState({ hasError: false }) },
          'Try again'
        )
      );
    }

    return this.props.children;
  }
}

// Export utility functions
export const logError = (error: Error, context?: ErrorContext) => errorLogger.logError(error, context);
export const logWarning = (message: string, context?: ErrorContext) => errorLogger.logWarning(message, context);
export const logInfo = (message: string, context?: ErrorContext) => errorLogger.logInfo(message, context);
export const logCalculationError = (calculatorType: string, operation: string, inputs: any, error: Error) => 
  errorLogger.logCalculationError(calculatorType, operation, inputs, error);
export const logValidationError = (field: string, value: any, validationRule: string, component: string) =>
  errorLogger.logValidationError(field, value, validationRule, component);
export const logUserAction = (action: string, component: string, additionalData?: any) =>
  errorLogger.logUserAction(action, component, additionalData);
export const logApiError = (endpoint: string, method: string, statusCode: number, error: Error) =>
  errorLogger.logApiError(endpoint, method, statusCode, error);

export const getErrorLogs = (filter?: { level?: string; component?: string; limit?: number }) => 
  errorLogger.getLogs(filter);
export const getErrorMetrics = () => errorLogger.getMetrics();
export const exportErrorLogs = () => errorLogger.exportLogs();
export const clearErrorLogs = () => errorLogger.clearLogs();

export default errorLogger;